export interface Deal {
    id: number
    value: number
    netValue: number
    title: string
    customer: Customer
    lines: DealLine[];
    payments: DealPayment[];
    type: DealType;
    currentInstalment: number;
    instalments: number;

    crmid: string;
    isManuall: boolean;
    subscriptionInfo: SubscriptionSystemDetailsDTO;
    overdueInfo: OverdueDetailsDTO;
    cancelInfo: DealCancelDetailsDTO;

    parentDeal?: Deal;
    notificationJobs?: NotificationJob[];
}

export interface SubscriptionSystemDetailsDTO {
    iHostId: string;
    subscriptionStartDate: string;
    subscriptionEndDate: string;
}

export interface OverdueDetailsDTO {
    overdueCategory: OverdueCategory;
    overdueCategorySetAt: Date;
    stopAccessEmailDate: Date;
    stopAccessCutoffDate: Date;
    stopedAccessDueToOverude: Date;

    cancelSubscriptionOnFailure: boolean;
    cancelThirdPartyOnFailure: boolean;
}

export enum OverdueCategory {
    NO_CATEGORY,
    NO_PAYER,
    SLOW_PAYER
}

export interface DealCancelDetailsDTO {
    PreviousThirdPartyInfo: string;
    SubscriptionStartDateBeforeCancel: Date;
    SubscriptionEndDateBeforeCancel: Date;
    CanceledOn: Date;
    CancelationReason: string;
}

export interface NotificationJob {
    id: number;
    dateUpdated: Date;
    notificationDate: Date;
    to: string;
    type: NotificationType;
    dateSent?: Date;
    errorMessage?: string;
    deal?: Deal;
}

export enum NotificationType {
    RenewalOffer,
    UpcomingRenewalNotification,
}

export enum DealType {
    NEW_CUSTOMER,
    RENEWAL,
    UPSELLING,
    MANUAL,
    NO_RENEWAL,
    OVERDUE
}

export enum FlowType {
    NEW_CUSTOMER,
    RENEWAL,
    UPSELLING,
    NO_RENEWAL,
    OVERDUE
}

export interface Customer {
    id?: number;
    vatNumber: string;
    name: string;
    legalName: string;
    phone: string;
    email: string;
    officialEmail: string;
    ihostid: string;
    address: string;
    city: string;
    country: string;
    zipCode: string;
    streetNumber: string;
    taxPolicy: CustomerTaxCategory;

    locality?: string;
    operatingPeriod?: string;
    venueTier?: string;
    venueType?: string;
}

export enum CustomerTaxCategory {
    UNSPECIFIED = 0,
    NORMAL = 1,
    SPECIAL = 2,
    EU = 3,
    NOT_EU = 4,
}

export interface DealLine {
    id: number
    crmId: number
    productName: string
    productDescription: string
    itemPrice: number
    totalPrice: number
    discount: number
    discountPercentage: number
    netPrice: number
    vat: number
    quantity: number
}

export interface DealPayment {
    id: number,
    datePaid: string,
    dueDate: string,
    failedAttempts: number,
    amount: number,
    index: number,
    done: boolean,
    isProcessing: boolean
    paymentMethod: PaymentMethod;

    processingSince?: Date;
    paymentIntentId: string;

    deal?: Deal;
}

export enum PaymentMethod {
    BankAcount,
    CreditCard,

    None = -1
}

export interface CustomerMasterData {
    localities: OptionValue[];
    venueTypes: OptionValue[];
    enterpriseTypes: OptionValue[];
    operatingPeriods: OptionValue[];
    venueTiers: OptionValue[];
}

export interface CustomerAccountingImportDTO {
    date: Date;
    totalEntries: number;
}

export interface CustomerAccountingInfo {
    id: number;
    date: Date;
    customerName: string;
    customerVAT: string;
    thirdPartyAmount: number;
    licenceAmount: number;
    totalAmount: number;
    relatedDeals?: Deal[];
    error: string;
    warning: string;
    notifications?: NotificationJob[];
}

export interface OptionValue {
    display: string;
    key: string;
}

export interface Campaign {
    id: number;
    name: string;
    isActive: boolean;
    dateActivated?: Date;

    createAllDealsAtActivation: boolean;
    crmCampaign: boolean;
    iHostCampaign: boolean;

    crmId: string;
    crmLabelName: string;
    crmLabelId: number;
    crmAudienceFilterId: number;
    crmUserClickedFilterId: number;
    crmFlowId: number;
    crmInitialStageIndex: number;
    crmInitialStageId: number;
    crmClickedStageIndex: number;
    crmClickedStageId: number;

    lines: CampaignDealLine[];
    createdDeals: CampaignCreatedDeal[];

    description?: TranslatableTerm[];
}

export interface TranslatableTerm {
    lang: string;
    value: string;
}

export interface CampaignDealLine {
    id: number;
    crmProductId: string;
    itemPrice: number;
    quantity: number;
    discountPercentage?: number;
    discount?: number;
    totalPrice?: number;
}

export interface CampaignCreatedDeal {
    id: number;
    dateCreated: Date;
    dateClicked?: Date;
    dealId: number;
}

export interface PagedRequest {
    page: number;
    pageSize: number;
    sort?: string;
}

export interface PagedResponse<T> extends PagedRequest {
    total: number;
    items: T[];
}

export interface DealPagedRequest extends PagedRequest {
    type: FlowType;
    filter?: string;
}

export interface DealPagedRequest extends PagedRequest {
    type: FlowType;
    filter?: string;
}

export interface PaymentsPagedRequest extends PagedRequest {
    orderBy?: string;
    orderASC?: boolean;

    from?: Date | undefined;
    to?: Date | undefined;
    types: FlowType[];
    paymentMethods: PaymentMethod[];
    filter?: string;
    includeCompleted?: boolean;
    includeScheduled?: boolean;
    includeFailed?: boolean;
    includeProcessing?: boolean;
    includePending?: boolean;
    includeNotScheduled?: boolean;
    failedAttemptsFrom?: number;
    failedAttemptsTo?: number;
}

export interface NotificationJobPagedRequest extends PagedRequest {
    from?: Date | undefined;
    to?: Date | undefined;
    orderBy?: string;
    orderASC?: boolean;
    filterByNotificationType?: NotificationType;
}